import React, { useState, useEffect } from 'react';
import { Row, Col, Statistic, Table, Tag, Empty, message, DatePicker, Select, Button } from 'antd';
import HeartLoader from '../../../components/shared/HeartLoader';
import { DownloadOutlined } from '@ant-design/icons';
import Chart from 'react-apexcharts';
import { usePageHeader } from '../../../layouts/AdminLayout';
import {
  StyledAnalyticsTabs,
  TabsAndFiltersRow,
  FiltersContainer,
  ChartCard,
  StatCard,
  ChartsRow,
  PageContainer,
  selectStyles,
  rangePickerStyles,
  valueColors,
  marginStyles
} from '../styled';
import api from '../../../services/api';
import { format, parseISO, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

const CHART_COLORS = ['#1890ff', '#108043', '#faad14', '#b91c1c', '#722ed1', '#13c2c2', '#eb2f96', '#fa8c16'];

// Helper function to extract data from API response
const extractData = (response) => {
  if (response?.data?.success !== undefined) {
    return response.data.success ? response.data.data : null;
  }
  return response?.data;
};

const VolunteersTab = ({ dateRange: propDateRange, timeRange: propTimeRange }) => {
  const { setPageHeader } = usePageHeader();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState('engagement');
  const [dateRange, setDateRange] = useState(propDateRange || [dayjs().subtract(1, 'year').toDate(), dayjs().toDate()]);
  const [quickRange, setQuickRange] = useState(propTimeRange || 'year');
  const [analyticsData, setAnalyticsData] = useState({
    engagement: null,
    experience: null,
    area: null,
    retention: null
  });

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {
        startDate: dayjs(dateRange[0]).format('YYYY-MM-DD'),
        endDate: dayjs(dateRange[1]).format('YYYY-MM-DD')
      };
      const response = await api.getBlob('/screening/exports/volunteer-report', params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `volunteer-report-${dayjs().format('YYYY-MM-DD')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success('Volunteer report exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      message.error('Failed to export volunteer report');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    setPageHeader({ title: 'Volunteer Analytics', count: null, countIcon: null });
  }, [setPageHeader]);

  const handleDateRangeChange = (dates) => {
    if (dates) {
      setDateRange([dates[0].toDate(), dates[1].toDate()]);
      setQuickRange('custom');
    }
  };

  const handleQuickRangeChange = (value) => {
    setQuickRange(value);
    const now = dayjs();
    let newRange;
    switch (value) {
      case '30days': newRange = [now.subtract(30, 'days').toDate(), now.toDate()]; break;
      case '3months': newRange = [now.subtract(3, 'months').toDate(), now.toDate()]; break;
      case '6months': newRange = [now.subtract(6, 'months').toDate(), now.toDate()]; break;
      case 'year': newRange = [now.subtract(1, 'year').toDate(), now.toDate()]; break;
      case 'Q1': newRange = [dayjs().startOf('year').toDate(), dayjs().startOf('year').month(2).endOf('month').toDate()]; break;
      case 'Q2': newRange = [dayjs().startOf('year').month(3).toDate(), dayjs().startOf('year').month(5).endOf('month').toDate()]; break;
      case 'Q3': newRange = [dayjs().startOf('year').month(6).toDate(), dayjs().startOf('year').month(8).endOf('month').toDate()]; break;
      case 'Q4': newRange = [dayjs().startOf('year').month(9).toDate(), dayjs().startOf('year').month(11).endOf('month').toDate()]; break;
      default: return;
    }
    setDateRange(newRange);
  };

  // Calculate default dates based on timeRange
  const getDateParams = () => {
    let startDate, endDate;
    
    if (dateRange && dateRange[0] && dateRange[1]) {
      startDate = format(dateRange[0], 'yyyy-MM-dd');
      endDate = format(dateRange[1], 'yyyy-MM-dd');
    } else {
      const now = new Date();
      switch (quickRange) {
        case 'today':
          startDate = format(now, 'yyyy-MM-dd');
          endDate = format(now, 'yyyy-MM-dd');
          break;
        case 'week':
          startDate = format(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');
          endDate = format(now, 'yyyy-MM-dd');
          break;
        case 'month':
          startDate = format(startOfMonth(now), 'yyyy-MM-dd');
          endDate = format(endOfMonth(now), 'yyyy-MM-dd');
          break;
        case 'quarter':
          startDate = format(subMonths(now, 3), 'yyyy-MM-dd');
          endDate = format(now, 'yyyy-MM-dd');
          break;
        case 'year':
          startDate = format(subMonths(now, 12), 'yyyy-MM-dd');
          endDate = format(now, 'yyyy-MM-dd');
          break;
        default:
          startDate = format(subMonths(now, 3), 'yyyy-MM-dd');
          endDate = format(now, 'yyyy-MM-dd');
      }
    }
    
    return { startDate, endDate };
  };

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange, quickRange, activeSubTab]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const { startDate, endDate } = getDateParams();
      
      // Fetch data based on active sub-tab
      const endpoints = {
        engagement: '/screening/analytics/volunteers/engagement',
        experience: '/screening/analytics/volunteers/experience',
        area: '/screening/analytics/volunteers/area-coverage',
        retention: '/screening/analytics/volunteers/retention'
      };

      const response = await api.get(endpoints[activeSubTab], { startDate, endDate });

      const data = extractData(response);
      setAnalyticsData(prev => ({
        ...prev,
        [activeSubTab]: data
      }));
    } catch (error) {
      console.error('Error fetching volunteer analytics:', error);
      message.error('Failed to load volunteer analytics');
    } finally {
      setLoading(false);
    }
  };

  // Volunteer Engagement Sub-tab
  const renderEngagement = () => {
    const data = analyticsData.engagement;
    if (!data) return <Empty description="No engagement data available" />;

    const { summary = {}, monthlyTrend = [], topVolunteers = [] } = data;

    // Monthly Trend Line Chart
    const trendCategories = monthlyTrend.map(item => item.month || item._id?.month || 'Unknown');
    const trendSeries = [{
      name: 'Active Volunteers',
      data: monthlyTrend.map(item => item.activeVolunteers || item.count || 0)
    }, {
      name: 'Total Hours',
      data: monthlyTrend.map(item => item.totalHours || 0)
    }];

    const trendOptions = {
      chart: {
        type: 'line',
        toolbar: { show: false },
        zoom: { enabled: false }
      },
      colors: ['#1890ff', '#108043'],
      stroke: { curve: 'smooth', width: 3 },
      markers: { size: 4 },
      xaxis: { categories: trendCategories },
      yaxis: [
        { title: { text: 'Active Volunteers' } },
        { opposite: true, title: { text: 'Total Hours' } }
      ],
      legend: { position: 'top' },
      tooltip: { shared: true }
    };

    // Top Volunteers table columns
    const topVolunteersColumns = [
      {
        title: 'Rank',
        key: 'rank',
        width: 60,
        render: (_, __, index) => (
          <Tag color={index < 3 ? 'gold' : 'default'}>#{index + 1}</Tag>
        )
      },
      {
        title: 'Volunteer',
        dataIndex: 'name',
        key: 'name'
      },
      {
        title: 'Events',
        dataIndex: 'eventCount',
        key: 'eventCount',
        sorter: (a, b) => (a.eventCount || 0) - (b.eventCount || 0)
      },
      {
        title: 'Hours',
        dataIndex: 'totalHours',
        key: 'totalHours',
        render: val => `${(val || 0).toFixed(1)}h`
      }
    ];

    return (
      <>
        <Row gutter={[16, 16]} style={marginStyles.mb24}>
          <Col xs={12} sm={6}>
            <StatCard>
              <Statistic
                title="Total Volunteers"
                value={summary.totalVolunteers || 0}
              />
            </StatCard>
          </Col>
          <Col xs={12} sm={6}>
            <StatCard>
              <Statistic
                title="Active This Period"
                value={summary.activeVolunteers || 0}
              />
            </StatCard>
          </Col>
          <Col xs={12} sm={6}>
            <StatCard>
              <Statistic
                title="Total Hours"
                value={(summary.totalHours || 0).toFixed(1)}
                suffix="hrs"
              />
            </StatCard>
          </Col>
          <Col xs={12} sm={6}>
            <StatCard>
              <Statistic
                title="Avg Hours/Volunteer"
                value={(summary.avgHoursPerVolunteer || 0).toFixed(1)}
                suffix="hrs"
              />
            </StatCard>
          </Col>
        </Row>

        <ChartsRow gutter={[16, 16]}>
          <Col xs={24} lg={14}>
            <ChartCard title="Monthly Engagement Trend">
              {monthlyTrend.length > 0 ? (
                <Chart
                  options={trendOptions}
                  series={trendSeries}
                  type="line"
                  height={320}
                />
              ) : (
                <Empty description="No trend data" />
              )}
            </ChartCard>
          </Col>
          <Col xs={24} lg={10}>
            <ChartCard title="Top Volunteers">
              <Table
                dataSource={topVolunteers.slice(0, 10)}
                columns={topVolunteersColumns}
                rowKey={(record, index) => record._id || record.id || index}
                pagination={false}
                size="small"
              />
            </ChartCard>
          </Col>
        </ChartsRow>
      </>
    );
  };

  // Volunteer Experience Sub-tab
  const renderExperience = () => {
    const data = analyticsData.experience;
    if (!data) return <Empty description="No experience data available" />;

    const { distribution = [], avgEventsByExperience = [] } = data;

    // Experience Distribution Donut
    const expLabels = distribution.map(item => item.level || item._id || 'Unknown');
    const expSeries = distribution.map(item => item.count || 0);

    const expPieOptions = {
      chart: { type: 'donut' },
      labels: expLabels,
      colors: ['#1890ff', '#108043', '#faad14', '#b91c1c', '#722ed1'],
      legend: { position: 'bottom' },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${val.toFixed(1)}%`
      },
      plotOptions: {
        pie: {
          donut: {
            size: '55%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Total',
                formatter: () => expSeries.reduce((a, b) => a + b, 0)
              }
            }
          }
        }
      }
    };

    // Avg Events by Experience Bar Chart
    const avgExpCategories = avgEventsByExperience.map(item => item.level || item._id || 'Unknown');
    const avgExpSeries = [{
      name: 'Avg Events',
      data: avgEventsByExperience.map(item => item.avgEvents || item.average || 0)
    }];

    const avgExpOptions = {
      chart: {
        type: 'bar',
        toolbar: { show: false }
      },
      colors: ['#1890ff'],
      plotOptions: {
        bar: {
          borderRadius: 4,
          columnWidth: '60%',
          distributed: true
        }
      },
      xaxis: { categories: avgExpCategories },
      yaxis: { title: { text: 'Avg Events Attended' } },
      dataLabels: { enabled: false },
      legend: { show: false }
    };

    return (
      <ChartsRow gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <ChartCard title="Experience Level Distribution">
            {distribution.length > 0 ? (
              <Chart
                options={expPieOptions}
                series={expSeries}
                type="donut"
                height={350}
              />
            ) : (
              <Empty description="No distribution data" />
            )}
          </ChartCard>
        </Col>
        <Col xs={24} lg={12}>
          <ChartCard title="Average Events by Experience Level">
            {avgEventsByExperience.length > 0 ? (
              <Chart
                options={avgExpOptions}
                series={avgExpSeries}
                type="bar"
                height={350}
              />
            ) : (
              <Empty description="No experience data" />
            )}
          </ChartCard>
        </Col>
      </ChartsRow>
    );
  };

  // Volunteer Area Sub-tab
  const renderArea = () => {
    const data = analyticsData.area;
    if (!data) return <Empty description="No area data available" />;

    const { byArea = [], eventsByArea = [] } = data;

    // Volunteers by Area Donut
    const areaLabels = byArea.map(item => item.area || item._id || 'Unknown');
    const areaSeries = byArea.map(item => item.count || 0);

    const areaPieOptions = {
      chart: { type: 'donut' },
      labels: areaLabels,
      colors: CHART_COLORS,
      legend: { position: 'bottom' },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${val.toFixed(1)}%`
      },
      plotOptions: {
        pie: {
          donut: {
            size: '55%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Total',
                formatter: () => areaSeries.reduce((a, b) => a + b, 0)
              }
            }
          }
        }
      }
    };

    // Events by Area Bar Chart
    const eventAreaCategories = eventsByArea.map(item => item.area || item._id || 'Unknown');
    const eventAreaSeries = [{
      name: 'Events',
      data: eventsByArea.map(item => item.eventCount || item.count || 0)
    }];

    const eventAreaOptions = {
      chart: {
        type: 'bar',
        toolbar: { show: false }
      },
      colors: ['#108043'],
      plotOptions: {
        bar: {
          borderRadius: 4,
          horizontal: true
        }
      },
      xaxis: { title: { text: 'Number of Events' } },
      yaxis: { categories: eventAreaCategories },
      dataLabels: { enabled: true }
    };

    return (
      <ChartsRow gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <ChartCard title="Volunteers by Area">
            {byArea.length > 0 ? (
              <Chart
                options={areaPieOptions}
                series={areaSeries}
                type="donut"
                height={350}
              />
            ) : (
              <Empty description="No area data" />
            )}
          </ChartCard>
        </Col>
        <Col xs={24} lg={12}>
          <ChartCard title="Events Served by Area">
            {eventsByArea.length > 0 ? (
              <Chart
                options={eventAreaOptions}
                series={eventAreaSeries}
                type="bar"
                height={350}
              />
            ) : (
              <Empty description="No event area data" />
            )}
          </ChartCard>
        </Col>
      </ChartsRow>
    );
  };

  // Volunteer Retention Sub-tab
  const renderRetention = () => {
    const data = analyticsData.retention;
    if (!data) return <Empty description="No retention data available" />;

    const { cohortAnalysis = [], retentionRate = 0, churnedVolunteers = [], newVolunteers = 0 } = data;

    // Cohort Analysis Line Chart
    const cohortCategories = cohortAnalysis.map(item => item.month || item._id || 'Unknown');
    const cohortSeries = [{
      name: 'Retained',
      data: cohortAnalysis.map(item => item.retained || 0)
    }, {
      name: 'New',
      data: cohortAnalysis.map(item => item.new || 0)
    }, {
      name: 'Churned',
      data: cohortAnalysis.map(item => item.churned || 0)
    }];

    const cohortOptions = {
      chart: {
        type: 'bar',
        toolbar: { show: false },
        stacked: true
      },
      colors: ['#108043', '#1890ff', '#b91c1c'],
      plotOptions: {
        bar: {
          borderRadius: 4,
          columnWidth: '60%'
        }
      },
      xaxis: { categories: cohortCategories },
      yaxis: { title: { text: 'Volunteers' } },
      legend: { position: 'top' },
      dataLabels: { enabled: false }
    };

    // Churned Volunteers Table
    const churnedColumns = [
      {
        title: 'Volunteer',
        dataIndex: 'name',
        key: 'name'
      },
      {
        title: 'Last Event',
        dataIndex: 'lastEventDate',
        key: 'lastEventDate',
        render: val => val ? format(parseISO(val), 'MMM d, yyyy') : 'N/A'
      },
      {
        title: 'Events Attended',
        dataIndex: 'eventCount',
        key: 'eventCount'
      }
    ];

    return (
      <>
        <Row gutter={[16, 16]} style={marginStyles.mb24}>
          <Col xs={12} sm={8}>
            <StatCard>
              <Statistic
                title="Retention Rate"
                value={(retentionRate * 100).toFixed(1)}
                suffix="%"
                valueStyle={{ color: retentionRate > 0.7 ? '#108043' : retentionRate > 0.5 ? '#faad14' : '#b91c1c' }}
              />
            </StatCard>
          </Col>
          <Col xs={12} sm={8}>
            <StatCard>
              <Statistic
                title="New Volunteers"
                value={newVolunteers}
                valueStyle={{ color: valueColors.info }}
              />
            </StatCard>
          </Col>
          <Col xs={12} sm={8}>
            <StatCard>
              <Statistic
                title="Churned"
                value={churnedVolunteers?.length || 0}
                valueStyle={{ color: valueColors.danger }}
              />
            </StatCard>
          </Col>
        </Row>

        <ChartsRow gutter={[16, 16]}>
          <Col xs={24} lg={14}>
            <ChartCard title="Volunteer Cohort Analysis">
              {cohortAnalysis.length > 0 ? (
                <Chart
                  options={cohortOptions}
                  series={cohortSeries}
                  type="bar"
                  height={320}
                />
              ) : (
                <Empty description="No cohort data" />
              )}
            </ChartCard>
          </Col>
          <Col xs={24} lg={10}>
            <ChartCard title="Recently Churned Volunteers">
              {churnedVolunteers && churnedVolunteers.length > 0 ? (
                <Table
                  dataSource={churnedVolunteers.slice(0, 10)}
                  columns={churnedColumns}
                  rowKey={(record, index) => record._id || record.id || index}
                  pagination={false}
                  size="small"
                />
              ) : (
                <Empty description="No churned volunteers" />
              )}
            </ChartCard>
          </Col>
        </ChartsRow>
      </>
    );
  };

  const subTabItems = [
    {
      key: 'engagement',
      label: 'Engagement'
    },
    {
      key: 'experience',
      label: 'Experience'
    },
    {
      key: 'area',
      label: 'Area'
    },
    {
      key: 'retention',
      label: 'Retention'
    }
  ];

  const renderContent = () => {
    if (loading) {
      return <HeartLoader size="48px" fullHeight />;
    }
    
    switch (activeSubTab) {
      case 'experience': return renderExperience();
      case 'area': return renderArea();
      case 'retention': return renderRetention();
      default: return renderEngagement();
    }
  };

  return (
    <PageContainer>
      <TabsAndFiltersRow className="filters-animate-in">
        <StyledAnalyticsTabs
          activeKey={activeSubTab}
          onChange={setActiveSubTab}
          items={subTabItems}
        />
        <FiltersContainer>
          <Select value={quickRange} onChange={handleQuickRangeChange} style={selectStyles} size="large">
            <Option value="30days">Last 30 days</Option>
            <Option value="3months">Last 3 months</Option>
            <Option value="6months">Last 6 months</Option>
            <Option value="year">Last year</Option>
            <Option value="Q1">Q1 (Jan-Mar)</Option>
            <Option value="Q2">Q2 (Apr-Jun)</Option>
            <Option value="Q3">Q3 (Jul-Sep)</Option>
            <Option value="Q4">Q4 (Oct-Dec)</Option>
            <Option value="custom">Custom</Option>
          </Select>
          <RangePicker
            value={[dayjs(dateRange[0]), dayjs(dateRange[1])]}
            onChange={handleDateRangeChange}
            format="MM/DD/YY"
            style={rangePickerStyles}
            size="large"
            allowClear={false}
          />
          <Button icon={<DownloadOutlined />} onClick={handleExport} loading={exporting} size="large">Export</Button>
        </FiltersContainer>
      </TabsAndFiltersRow>

      <div className="content-animate-in">
        {renderContent()}
      </div>
    </PageContainer>
  );
};

export default VolunteersTab;
